import { useId, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContentBlockProps {
  icon?: LucideIcon;
  title?: string;
  tone?: "plain" | "emphasis";
  children: ReactNode;
  className?: string;
}

const TONE_CLASS = {
  plain: undefined,
  emphasis: "rounded-2xl bg-accent px-6 py-6",
} as const;

/**
 * Labeled reading unit for course storyboard blocks — icon + title mark
 * where a didactic unit starts. Emphasis tone is reserved for summaries.
 */
const ContentBlock = ({
  icon: Icon,
  title,
  tone = "plain",
  children,
  className,
}: ContentBlockProps) => {
  const headingId = useId();
  const heading = title?.trim() || undefined;
  const hasHeader = Boolean(Icon || heading);
  const Root = heading ? "section" : "div";

  return (
    <Root
      aria-labelledby={heading ? headingId : undefined}
      className={cn(TONE_CLASS[tone], className)}
    >
      {hasHeader && (
        <div className="mb-4 flex items-center gap-3">
          {Icon && (
            <div
              className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-white"
              aria-hidden="true"
            >
              <Icon className="size-5 text-primary" />
            </div>
          )}
          {heading && <h2 id={headingId}>{heading}</h2>}
        </div>
      )}
      {children}
    </Root>
  );
};

export default ContentBlock;
